import { message } from "antd";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  deletePaymentMethod,
  getWalletDetail,
  setDefaultPaymentMethod,
} from "@/api/payment";
import ManagePaymentMethodsModal from "@/components/ChargeModal/ManagePaymentMethodsModal";
import AirwallexCardForm from "@/components/modal/AirwallexCardForm";
import {
  PaymentProvider,
  SavedPaymentMethod,
} from "@/components/ChargeModal/types";
import { useModalStore } from "@/hooks/useModalStore";
import { usePaymentStore } from "@/hooks/usePaymentStore";

const toSavedPaymentMethods = (
  source: any[] | undefined,
  provider: PaymentProvider
): SavedPaymentMethod[] =>
  (source || []).map((method, index) => {
    const card = method?.card || method;
    const expiryMonth = card?.exp_month || card?.expMonth || method?.expMonth;
    const expiryYear = card?.exp_year || card?.expYear || method?.expYear;
    const paymentMethodId =
      method?.id ||
      method?.paymentMethodId ||
      method?.stripePaymentMethodId ||
      method?.awxPaymentMethodId;

    return {
      id: paymentMethodId || `${provider}-${card?.last4 || method?.last4 || "card"}-${index}`,
      paymentMethodId,
      provider,
      brand: card?.brand || method?.cardBrand || method?.brand || "Card",
      last4: card?.last4 || method?.last4 || "----",
      expires:
        expiryMonth && expiryYear
          ? `${String(expiryMonth).padStart(2, "0")} / ${String(expiryYear).slice(-2)}`
          : undefined,
      isDefault: method?.isDefault === true,
    };
  });

export default function ManagePaymentMethodsGlobalModal() {
  const { t } = useTranslation();
  const { walletDetail, setWalletDetail } = usePaymentStore();
  const {
    showManagePaymentMethodsModal,
    setShowManagePaymentMethodsModal,
    setShowModalPaymentBoundCard,
    showAirwallexCardForm,
    setShowAirwallexCardForm,
  } = useModalStore();

  const methodsByProvider = useMemo(
    () => ({
      airwallex: toSavedPaymentMethods(
        walletDetail?.awxPaymentMethods,
        "airwallex"
      ),
      stripe: toSavedPaymentMethods(walletDetail?.paymentMethods, "stripe"),
    }),
    [walletDetail?.awxPaymentMethods, walletDetail?.paymentMethods]
  );

  return (
    <>
      <ManagePaymentMethodsModal
        open={showManagePaymentMethodsModal}
        methodsByProvider={methodsByProvider}
        onClose={() => setShowManagePaymentMethodsModal(false)}
        onAdd={(provider) => {
          setShowManagePaymentMethodsModal(false);
          if (provider === "airwallex") {
            setShowAirwallexCardForm(true);
            return;
          }
          setShowModalPaymentBoundCard(true);
        }}
        onRemove={async (id, provider) => {
          if (!provider) return;

          await deletePaymentMethod(
            id,
            provider === "airwallex" ? "awx" : "stripe"
          );
          try {
            const updatedWalletDetail = await getWalletDetail({
              forceRefresh: true,
            });
            setWalletDetail(updatedWalletDetail);
            message.success(
              t("components.chargeModal.managePaymentMethods.removeSuccess", {
                defaultValue: "Payment method removed.",
              })
            );
          } catch {
            message.warning(
              t(
                "components.chargeModal.managePaymentMethods.removeSuccessRefreshFailed",
                {
                  defaultValue:
                    "Payment method removed. Refresh the page to view the latest list.",
                }
              )
            );
          }
        }}
        onSetDefault={async (id, provider) => {
          if (!provider) return;

          await setDefaultPaymentMethod(
            id,
            provider === "airwallex" ? "awx" : "stripe"
          );
          try {
            const updatedWalletDetail = await getWalletDetail({
              forceRefresh: true,
            });
            setWalletDetail(updatedWalletDetail);
            message.success(
              t("components.chargeModal.managePaymentMethods.setDefaultSuccess", {
                defaultValue: "Default payment method updated.",
              })
            );
          } catch {
            message.warning(
              t(
                "components.chargeModal.managePaymentMethods.setDefaultSuccessRefreshFailed",
                {
                  defaultValue:
                    "Default payment method updated. Refresh the page to view the latest list.",
                }
              )
            );
          }
        }}
      />
      <AirwallexCardForm
        visible={showAirwallexCardForm}
        onClose={() => setShowAirwallexCardForm(false)}
      />
    </>
  );
}
